import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';

interface ICheckboxOption<V extends string | number = string> {
  value: V,
  name: string,
  disabled?: boolean,
}

type TArrayKeys<T> = { [K in keyof T]: T[K] extends (string | number)[] ? K : never }[keyof T];

interface IUseCheckboxFilterProps<T, V extends string | number = string> {
  filterKey: TArrayKeys<T>,
  items: ICheckboxOption<V>[],
  normalizeSelection?: (selected: V[], allValues: V[]) => V[],
}

const createUseCheckboxFilter = <T>(
  useFilterContext: () => { localFilters: T, setLocalFilters: (updater: (prev: T) => T) => void },
) => <V extends string | number = string>({ filterKey, items, normalizeSelection }: IUseCheckboxFilterProps<T, V>) => {
  const { localFilters, setLocalFilters } = useFilterContext();
  const allValues = useMemo(() => items.map((item) => item.value), [items]);

  const [selectedValues, setSelectedValues] = useState<V[]>(() => {
    const raw = (localFilters[filterKey] as V[]) ?? [];
    return normalizeSelection && allValues.length > 0 ? normalizeSelection(raw, allValues) : raw;
  });

  useEffect(() => {
    if (!normalizeSelection || allValues.length === 0) return;

    const next = normalizeSelection(selectedValues, allValues);
    if (isEqual(next, selectedValues)) return;

    setSelectedValues(next);
    setLocalFilters((prevFilters) => ({ ...prevFilters, [filterKey]: next }));
  }, [allValues, normalizeSelection, filterKey, setLocalFilters, selectedValues]);

  const toggle = useCallback((value: V) => {
    setSelectedValues((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      setLocalFilters((prevFilters) => ({ ...prevFilters, [filterKey]: next }));
      return next;
    });
  }, [filterKey, setLocalFilters]);

  const toggleAll = useCallback(() => {
    setSelectedValues((prev) => {
      const next = prev.length === allValues.length ? [] : allValues;
      setLocalFilters((prevFilters) => ({ ...prevFilters, [filterKey]: next }));
      return next;
    });
  }, [filterKey, setLocalFilters, allValues]);

  const clear = useCallback(() => {
    setSelectedValues([]);
    setLocalFilters((prevFilters) => ({ ...prevFilters, [filterKey]: [] }));
  }, [filterKey, setLocalFilters]);

  return {
    items,
    selectedValues,
    isAllSelected: selectedValues.length === allValues.length && allValues.length > 0,
    isIndeterminate: selectedValues.length > 0 && selectedValues.length < allValues.length,
    toggle,
    toggleAll,
    clear,
  };
};

export { createUseCheckboxFilter };
export type { ICheckboxOption };
